import React, { CSSProperties, useEffect, useRef, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { InView } from 'react-intersection-observer';
import { MessageInfo } from "@42maru/webchat";
import { MessageHistoryCustom } from '@/model';
import { UTCToLocalString } from "@/util/dateUtil";
import { getValueFromDotJoinKey, getValueFromGlobalContext } from "@/util/messageUtil";
import { getTextFromResponse } from "@/util/messageUtil";
import { isTextLineClamped } from '@/util/stringUtil';
import Tooltip from "@/component/Tooltip";
import TextBadge from '@/component/TextBadge';
import { successToast } from "@/component/Toast";
import ChannelThumb from "@/component/ChannlThumb";
import SortButton from '@/component/SortButton';
import HighlightText from '@/component/HighlightText';
import { PasteIcon, FeedbackGoodIcon, FeedbackBadIcon } from "@/assets/svgs/icons";


interface MessageHistoryTableProps {
    messages: MessageInfo[];
    searchKeyword: string;
    searchType: "user_query" | "bot_query" | string;
    tableBodyRef: React.RefObject<HTMLDivElement>;
    sort: "asc" | "desc";
    additionalColumns: MessageHistoryCustom[];
    onScrollToBottom: () => void;
    onClickSort: () => void;
}

const MessageHistoryTable: React.FC<MessageHistoryTableProps> = (props) => {
    const { messages, searchKeyword, searchType, tableBodyRef, sort, additionalColumns, onClickSort, onScrollToBottom } = props;

    const intl = useIntl();

    const gridStyle = useMemo<CSSProperties>(() => {
        let cloumnWidths: string[] = [];
        if (!messages?.length) {
            cloumnWidths = ["150px", ...Array.from(additionalColumns, () => "90px"), "190px", "minmax(220px,1fr)", "85px", "100px", "100px", "90px", "70px", "90px"];
        } else {
            cloumnWidths = ["150px", ...Array.from(additionalColumns, () => "120px"), "240px", "minmax(306px,1fr)", "140px", "176px", "104px", "104px", "80px", "88px"];
        }
        return {
            gridTemplateColumns: cloumnWidths.join(" ")
        };
    }, [additionalColumns, messages?.length]);

    return (
        <div role="table"
             aria-label={intl.formatMessage({ id: "i07001" })}
             className="selection-table min-w-fit"
             style={gridStyle}
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     aria-sort={sort === "asc" ? "ascending" : "descending"}
                     className="selection-table-th group pl-[25px]"
                >
                    <SortButton order={sort}
                                aria-label={`${intl.formatMessage({ id: "i07007" })} ${sort === "asc" ? "오름차순" : "내림차순"}`}
                                onClick={onClickSort}
                    >
                        {intl.formatMessage({ id: "i07007" })}
                    </SortButton>
                </div>
                {additionalColumns.map(column => (
                    <div role="columnheader"
                         className="selection-table-th pl-[15px]"
                         key={column.key}
                    >
                        {column.label}
                    </div>
                ))}
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07005" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07006" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07008" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07009" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i07004" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[25px]"
                >
                    {intl.formatMessage({ id: "i07010" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07011" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i07012" })}
                </div>
            </div>
            <div ref={tableBodyRef}
                 role="rowgroup"
                 className="selection-table-tbody auto-rows-max"
            >
                {messages?.map(message => (
                    <MessageHistoryTableFragment key={message.id}
                                                 message={message}
                                                 searchKeyword={searchKeyword}
                                                 searchType={searchType}
                                                 additionalColumns={additionalColumns}
                    />
                ))}
                {!messages?.length && (
                    <div role="status"
                         className="empty-notice col-span-full"
                    >
                        {intl.formatMessage({ id: searchKeyword ? "b0010" : "b0013" })}
                    </div>
                )}
                <InView as="div"
                       aria-hidden="true"
                       className="h-px"
                       onChange={inView => inView && onScrollToBottom()}
                />
            </div>
        </div>
    );
};

interface MessageHistoryTableFragmentProps {
    message: MessageInfo;
    searchKeyword?: string;
    searchType?: "user_query" | "bot_query" | string;
    additionalColumns: MessageHistoryCustom[];
}

const MessageHistoryTableFragment: React.FC<MessageHistoryTableFragmentProps> = (props) => {
    const { message, searchKeyword, searchType, additionalColumns } = props;
    const intl = useIntl();

    const conversationId = message.conversation_id ?? "";
    const userKey = message.context?.system?.global?.user_key ?? "";
    const responseText = getTextFromResponse(message.messages);

    const getCustomKeyValue = (message: MessageInfo, key: string) => {
        const value: any = getValueFromDotJoinKey(message, key);
        return typeof value !== "object" ? value : JSON.stringify(value);
    };

    const renderTooltip = (text: string, message: string) => (
        <button aria-label={intl.formatMessage({ id: "b0062" })}
                className="flex gap-2 items-center text-[14px]"
                onClick={() => navigator.clipboard.writeText(text).then(() => successToast(message))}
        >
            {text}
            <PasteIcon aria-hidden="true"
                       className="size-4"
            />
        </button>
    );

    return (
        <div role="row"
             className="selection-table-tr no-hover items-start"
        >
            <div role="cell"
                 className="selection-table-td h-full pl-[25px] flex items-center number-cell"
            >
                {message.created_at && UTCToLocalString(message.created_at, "YYYY-MM-DD HH:mm")}
            </div>
            {additionalColumns.map(column => (
                <div key={column.key}
                     role="cell"
                     className="selection-table-td h-full pl-[15px] flex items-center"
                >
                    {getCustomKeyValue(message, column.key) ?? ""}
                </div>
            ))}
            <div role="cell"
                 className="selection-table-td h-full pl-[15px]"
            >
                <ClampedTextTooltip text={responseText}>
                    {ref => (
                        <HighlightText ref={ref}
                                       query={message.query ?? ""}
                                       keyword={searchType === "user_query" ? searchKeyword : undefined}
                                       className="whitespace-pre-wrap line-clamp-2"
                                       highlightClassName="text-highlight"
                        />
                    )}
            </ClampedTextTooltip>
            </div>
            <div role="cell"
                 className="selection-table-td h-full min-h-fit pl-[15px]"
            >
                <ClampedTextTooltip text={responseText}>
                    {ref => (
                        <HighlightText ref={ref}
                                       query={responseText.trim()}
                                       keyword={searchType === "bot_query" ? searchKeyword : undefined}
                                       className="whitespace-pre-wrap line-clamp-2"
                                       highlightClassName="text-highlight"
                        />
                    )}
                </ClampedTextTooltip>
            </div>
            <div role="cell"
                 className="selection-table-td h-full px-[15px] flex items-center truncate"
            >
                {getValueFromGlobalContext(message, "intent")?.name ?? ""}
            </div>
            <div role="cell"
                 className="selection-table-td h-full pl-[15px] py-[7px] flex items-center"
            >
                <TextBadge className="max-w-fit w-full px-2 py-1 rounded-[6px] truncate"
                           text={message.scene_name ?? ""}
                />
            </div>
            <div role="cell"
                 className="selection-table-td h-full pl-[25px] flex items-center"
            >
                <Tooltip placement="bottom-start"
                         message={renderTooltip(conversationId, intl.formatMessage({ id: "i07013" }))}
                >
                    <div className="w-full truncate number-cell">
                        {conversationId}
                    </div>
                </Tooltip>
            </div>
            <div role="cell"
                 className="selection-table-td h-full pl-[25px] flex items-center number-cell"
            >
                <Tooltip placement="bottom-start"
                         message={renderTooltip(userKey, intl.formatMessage({ id: "i07014" }))}
                >
                    <div className="w-full truncate">
                        {userKey}
                    </div>
                </Tooltip>
            </div>
            <div role="cell"
                 className="selection-table-td h-full pl-[15px] flex items-center"
            >
                <ChannelThumb channel={getValueFromGlobalContext(message, "channel")}
                              className="size-6"
                />
                <span className="sr-only">{getValueFromGlobalContext(message, "channel") || "-"}</span>
            </div>
            <div role="cell"
                 className="selection-table-td h-full pl-[15px] flex items-center"
            >
                {message.feedback === "good" && (<>
                    <FeedbackGoodIcon aria-hidden="true"
                                      className="ml-3 size-4.5"
                    />
                    <span className="sr-only">{intl.formatMessage({ id: "i01015" })}</span>
                </>)}
                {message.feedback === "bad" && (<>
                    <FeedbackBadIcon aria-hidden="true"
                                     className="ml-3 size-4.5"
                    />
                    <span className="sr-only">{intl.formatMessage({ id: "i01016" })}</span>
                </>)}
            </div>
        </div>
    );
};

interface ClampedTextTooltipProps {
    text: string;
    children: (ref: React.RefObject<HTMLSpanElement>) => React.ReactNode;
}

const ClampedTextTooltip: React.FC<ClampedTextTooltipProps> = (props) => {
    const { text, children } = props;

    const contentRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState<boolean>(false);

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }
        setIsTruncated(isTextLineClamped(contentRef.current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentRef]);

    return (
        <div className="relative size-full flex items-center">
            <Tooltip message={text}
                    placement="bottom-start"
                    disable={!isTruncated}
                    tooltipClassName="-ml-[15px] h-[44px] flex items-center text-[16px] font-[400]"
            >
                {children(contentRef)}
            </Tooltip>
        </div>
    );
};


export default MessageHistoryTable;
